import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  createInvalidationChannel,
  type InvalidationChannel,
} from "../channels/invalidation";

const InvalidationContext = createContext<InvalidationChannel | null>(null);

export const InvalidationProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<InvalidationChannel | null>(null);

  useEffect(() => {
    const nextChannel = createInvalidationChannel();
    const unsubscribe = nextChannel.subscribe(({ queryKey }) => {
      void queryClient.invalidateQueries({ queryKey });
    });
    setChannel(nextChannel);

    return () => {
      unsubscribe();
      nextChannel.close();
    };
  }, [queryClient]);

  return (
    <InvalidationContext.Provider value={channel}>
      {children}
    </InvalidationContext.Provider>
  );
};

/**
 * Invalidates here and in every other `/pro` window. Without the provider it
 * degrades to a plain local invalidation, so widget tests need no wrapper.
 */
export const useInvalidate = () => {
  const channel = useContext(InvalidationContext);
  const queryClient = useQueryClient();

  return useCallback(
    (filters: { queryKey: readonly unknown[] }) => {
      void queryClient.invalidateQueries({ queryKey: filters.queryKey });
      channel?.post({ queryKey: filters.queryKey });
    },
    [channel, queryClient],
  );
};
